import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ATTACHMENT_OPTIONS,
  ATTACHMENT_RATE_ELIGIBLE,
  RATE_FIELDS,
} from '../../utils/machineRegistrationConfig';
import {
  approveMachineRegistration,
  fetchPendingMachineRegistrations,
  getMachineRegistrationDocumentUrl,
  rejectMachineRegistration,
} from '../../services/machineRegistrationApi';

const DOC_LABELS = {
  rcBook: 'RC Book',
  insurance: 'Insurance',
  pollution: 'Pollution',
  identity: 'Identity',
  chassisPlate: 'Chassis plate',
  machineInvoice: 'Invoice',
  equipmentPhoto: 'Equipment photo',
};

function attachmentSummary(reg) {
  const ids = Array.isArray(reg.attachments) ? reg.attachments : [];
  if (!ids.length) return '—';
  return ids
    .map((id) => ATTACHMENT_OPTIONS.find((o) => o.id === id)?.label || id)
    .join(', ');
}

function buildRateDraft(reg) {
  return {
    machineId: '',
    rate_per_hour: reg.rate_per_hour ?? '',
    rate_per_day: reg.rate_per_day ?? '',
    rate_per_week: reg.rate_per_week ?? '',
    rate_per_month: reg.rate_per_month ?? '',
    attachment_rates: { ...(reg.attachment_rates || {}) },
    attachment_min_hours: { ...(reg.attachment_min_hours || {}) },
  };
}

function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

export default function MachineRegistrationApprovals({ onQueueChange, onApproved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [rateDrafts, setRateDrafts] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPendingMachineRegistrations('pending');
      const list = data.items || [];
      setItems(list);
      setRateDrafts(Object.fromEntries(list.map((reg) => [reg.id, buildRateDraft(reg)])));
      if (onQueueChange) onQueueChange(list.length);
    } catch {
      setItems([]);
      setRateDrafts({});
      if (onQueueChange) onQueueChange(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (regId, patch) => {
    setRateDrafts((prev) => ({
      ...prev,
      [regId]: { ...prev[regId], ...patch },
    }));
  };

  const updateAttachmentRate = (regId, attachId, value) => {
    setRateDrafts((prev) => {
      const draft = prev[regId] || {};
      const nextRates = { ...(draft.attachment_rates || {}) };
      const nextMins = { ...(draft.attachment_min_hours || {}) };
      if (!value) {
        delete nextRates[attachId];
        delete nextMins[attachId];
      } else {
        nextRates[attachId] = Number(value);
      }
      return {
        ...prev,
        [regId]: {
          ...draft,
          attachment_rates: nextRates,
          attachment_min_hours: nextMins,
        },
      };
    });
  };

  const updateAttachmentMinHours = (regId, attachId, value) => {
    setRateDrafts((prev) => {
      const draft = prev[regId] || {};
      const nextMins = { ...(draft.attachment_min_hours || {}) };
      if (!value) delete nextMins[attachId];
      else nextMins[attachId] = Number(value);
      return {
        ...prev,
        [regId]: { ...draft, attachment_min_hours: nextMins },
      };
    });
  };

  const handleViewDoc = async (registrationId, docType) => {
    try {
      const url = await getMachineRegistrationDocumentUrl(registrationId, docType);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      else alert('Document not available');
    } catch (err) {
      alert(err.message || 'Could not open document');
    }
  };

  const handleApprove = async (reg) => {
    const draft = rateDrafts[reg.id] || buildRateDraft(reg);
    const ratePerHour = Number(draft.rate_per_hour);
    if (!ratePerHour || ratePerHour <= 0) {
      alert('Hourly rate is required before approval');
      return;
    }

    setBusyId(reg.id);
    try {
      const body = {
        ratePerHour,
        attachmentRates: draft.attachment_rates || {},
        attachmentMinHours: draft.attachment_min_hours || {},
      };
      if (draft.machineId?.trim()) body.machineId = draft.machineId.trim();
      const ratePerDay = Number(draft.rate_per_day);
      const ratePerWeek = Number(draft.rate_per_week);
      const ratePerMonth = Number(draft.rate_per_month);
      if (ratePerDay > 0) body.ratePerDay = ratePerDay;
      if (ratePerWeek > 0) body.ratePerWeek = ratePerWeek;
      if (ratePerMonth > 0) body.ratePerMonth = ratePerMonth;

      const result = await approveMachineRegistration(reg.id, body);
      if (onApproved) onApproved(result?.machine);
      await load();
    } catch (err) {
      alert(err.message || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    const note = window.prompt('Rejection reason (required — sent to the owner):');
    if (note == null) return;
    const trimmed = String(note).trim();
    if (!trimmed) {
      alert('Rejection reason is required.');
      return;
    }
    setBusyId(id);
    try {
      await rejectMachineRegistration(id, trimmed);
      await load();
    } catch (err) {
      alert(err.message || 'Reject failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading machine registrations…</p>;
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No pending machine registration requests.</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((reg) => {
        const docKeys = Object.keys(reg.documents || {});
        const draft = rateDrafts[reg.id] || buildRateDraft(reg);
        const eligibleAttachments = (reg.attachments || []).filter((a) => ATTACHMENT_RATE_ELIGIBLE.includes(a));

        return (
          <div
            key={reg.id}
            className="rounded-lg border border-[#C9A84C]/25 bg-[#C9A84C]/5 p-3 text-sm"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-[#E8D5A3]">
                  {reg.machine_name} · {reg.machine_type}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Owner: {reg.owner_name || '—'} · {reg.owner_phone || '—'} · {reg.owner_email || '—'}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {reg.mobility_class === 'tracked' ? 'Crawler' : 'Wheeled'}
                  {reg.reg_no && reg.reg_no !== 'TRACKED-NO-RTO' ? ` · ${reg.reg_no}` : ''}
                  {reg.year ? ` · ${reg.year}` : ''}
                  {reg.capacity ? ` · ${reg.capacity}` : ''}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Attachments: {attachmentSummary(reg)}
                  {reg.attachment_notes ? ` (${reg.attachment_notes})` : ''}
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  Submitted {new Date(reg.created_at).toLocaleString('en-IN')}
                </div>
                {docKeys.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {docKeys.map((docType) => (
                      <Button
                        key={docType}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[10px]"
                        onClick={() => handleViewDoc(reg.id, docType)}
                      >
                        {DOC_LABELS[docType] || docType}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-white/10 bg-black/20 p-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#C9A84C] mb-3">
                Owner rate chart — edit before approve
              </p>

              <div className="mb-3">
                <Label className="mb-1 block text-xs text-white/70">Fleet machine ID (optional)</Label>
                <Input
                  placeholder="Auto-generate if blank"
                  className="h-8 text-xs"
                  value={draft.machineId || ''}
                  onChange={(e) => updateDraft(reg.id, { machineId: e.target.value })}
                />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {RATE_FIELDS.map((field) => (
                  <div key={field.key}>
                    <Label className="mb-1 block text-xs text-white/70">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/50">₹</span>
                      <Input
                        type="number"
                        min="0"
                        className="h-8 text-xs"
                        placeholder={field.required ? 'Required' : 'Optional'}
                        value={draft[field.key] ?? ''}
                        onChange={(e) => updateDraft(reg.id, {
                          [field.key]: e.target.value ? Number(e.target.value) : '',
                        })}
                      />
                      <span className="text-[10px] text-white/40 whitespace-nowrap">{field.suffix}</span>
                    </div>
                    {!reg.rate_per_hour && field.key === 'rate_per_hour' && (
                      <p className="text-[10px] text-amber-300/80 mt-1">Owner did not set — enter before approve</p>
                    )}
                  </div>
                ))}
              </div>

              {eligibleAttachments.length > 0 && (
                <div className="mt-3 border-t border-white/10 pt-3">
                  <p className="text-xs font-medium text-white/70 mb-2">Attachment extra rates (/ hr)</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    {eligibleAttachments.map((attachId) => {
                      const label = ATTACHMENT_OPTIONS.find((o) => o.id === attachId)?.label || attachId;
                      return (
                        <div key={attachId} className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-white/55 flex-1 min-w-0 truncate">{label}</span>
                            <Input
                              type="number"
                              min="0"
                              className="h-8 w-24 text-xs"
                              placeholder="₹/hr"
                              value={draft.attachment_rates?.[attachId] ?? ''}
                              onChange={(e) => updateAttachmentRate(reg.id, attachId, e.target.value)}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-white/45 flex-1 min-w-0 truncate">
                              Min usage hours
                            </span>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              className="h-8 w-24 text-xs"
                              placeholder="Optional"
                              value={draft.attachment_min_hours?.[attachId] ?? ''}
                              onChange={(e) => updateAttachmentMinHours(reg.id, attachId, e.target.value)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Number(draft.rate_per_hour) > 0 && (
                <p className="mt-2 text-[10px] text-white/45">
                  Preview: {formatInr(draft.rate_per_hour)}/hr
                  {eligibleAttachments
                    .filter((id) => draft.attachment_rates?.[id])
                    .map((id) => {
                      const extra = draft.attachment_rates[id];
                      const label = ATTACHMENT_OPTIONS.find((o) => o.id === id)?.label || id;
                      return ` · +${formatInr(extra)}/hr (${label})`;
                    })
                    .join('')}
                </p>
              )}
            </div>

            <div className="mt-3 flex shrink-0 justify-end gap-2">
              <Button
                type="button"
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={busyId === reg.id}
                onClick={() => handleApprove(reg)}
              >
                Approve & activate
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                disabled={busyId === reg.id}
                onClick={() => handleReject(reg.id)}
              >
                Reject
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
